import { type DataRow } from '@/store/chartAtoms'

/**
 * Attempts to parse a plaintext block containing ChatGPT usage log entries (as copied from the UI)
 * into a tabular structure suitable for chart creation.
 *
 * Each entry is assumed to start with a line like:
 *   "Jun 14, 09:25 PM    You    Included in Pro    Yes    o3"
 * followed (not necessarily immediately) by a line that begins with "Total:" and then
 * a line that contains the cost value such as "$0.08".
 *
 * For every entry that contains both a timestamp line and a cost value, a DataRow is emitted
 * with these two columns:
 *   - Timestamp (original timestamp string)
 *   - Cost (numeric value, in dollars)
 *
 * If the input does not appear to follow this pattern, the function returns `null` so that
 * the caller can fall back to other parsing strategies.
 */
export function parseChatGptCostLog(input: string): { columns: string[]; rows: DataRow[]; title: string } | null {
  if (!input) return null

  const dateRegex = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2},/i
  const lines = input.split(/\r?\n/)

  interface PartialEntry {
    timestampLine: string
    cost?: number
  }

  const entries: PartialEntry[] = []
  let current: PartialEntry | null = null

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()

    // Detect the beginning of a new entry by a line that starts with a month name
    if (dateRegex.test(line)) {
      // If we were building a previous entry, push it before starting a new one
      if (current) {
        entries.push(current)
      }
      current = { timestampLine: line }
      continue
    }

    // Within an entry, look for a line that starts with "Total:" then expect the next non-empty line
    if (current && line.toLowerCase().startsWith('total:')) {
      // Seek forward for the next line that looks like a dollar amount
      for (let j = i + 1; j < lines.length; j++) {
        const next = lines[j].trim()
        if (next === '') continue
        const match = next.match(/\$\s*(\d+(?:\.\d+)?)/)
        if (match) {
          current.cost = parseFloat(match[1])
          // Move outer loop index forward so we don\'t process these lines again
          i = j
        }
        break
      }
    }
  }

  // Push the last pending entry
  if (current) entries.push(current)

  // Filter to those that have a cost value
  const completeEntries = entries.filter((e) => e.cost !== undefined)
  if (completeEntries.length === 0) return null

  // Build DataRows
  const monthMap: Record<string, number> = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
  }

  const rows: DataRow[] = completeEntries.map((e) => {
    const tsRegex = /^(?<mon>Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(?<day>\d{1,2}),?\s*(?<time>\d{1,2}:\d{2})\s*(?<ampm>[AP]M)/i
    const match = e.timestampLine.match(tsRegex)
    let isoString = e.timestampLine // fallback
    if (match && match.groups) {
      try {
        const { mon, day, time, ampm } = match.groups as any
        const [hourStr, minuteStr] = time.split(':')
        let hour = parseInt(hourStr, 10)
        const minute = parseInt(minuteStr, 10)
        if (ampm.toUpperCase() === 'PM' && hour !== 12) hour += 12
        if (ampm.toUpperCase() === 'AM' && hour === 12) hour = 0

        const monthIndex = monthMap[mon.substring(0,3)]
        const year = new Date().getFullYear()
        const dateObj = new Date(Date.UTC(year, monthIndex, Number(day), hour, minute))
        isoString = dateObj.toISOString()
      } catch {
        /* ignore parse errors and keep raw */
      }
    }

    return {
      Timestamp: isoString,
      Cost: e.cost as number,
    }
  })

  return {
    columns: ['Timestamp', 'Cost'],
    rows,
    title: 'ChatGPT Cost Over Time',
  }
} 